import { useCallback, useState } from 'react';

import { MessageInstance } from 'antd/es/message/interface';
import { Dayjs } from 'dayjs';
import i18n from 'i18next';

import { ApiErrorData } from 'models/ApiResponse';

import { KitCampaignService } from 'services/KitCampaignService';

import {
  AggregateSectionData,
  DashboardKitData,
  TurnCountByTaskData,
  TurnProgressByDayData,
  TurnSummaryStatusGridData,
} from '../types';

const BASE = 'scenes.kit.dashboard.hooks.use_dashboard_data';

interface UseDashboardDataParams {
  message: MessageInstance;
}

export interface UseDashboardDataReturn {
  aggregateSection?: AggregateSectionData;
  turnCountByTask?: TurnCountByTaskData[];
  turnSummaryStatusGrid?: TurnSummaryStatusGridData[];
  turnProgressByDay?: TurnProgressByDayData;
  dataLoading: boolean;
  fetchDashboardData: (
    dateRange: [Dayjs, Dayjs],
    campaignIds: number[]
  ) => Promise<void>;
}

export function useDashboardData({
  message,
}: UseDashboardDataParams): UseDashboardDataReturn {
  const [dashboardData, setDashboardData] = useState<
    DashboardKitData | undefined
  >(undefined);
  const [dataLoading, setDataLoading] = useState(false);

  const fetchDashboardData = useCallback(
    async (dateRange: [Dayjs, Dayjs], campaignIds: number[]) => {
      setDataLoading(true);

      const response = await KitCampaignService.getDashboardKitData({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        taskIds: campaignIds.length > 0 ? campaignIds : undefined,
      });

      if (response.success) {
        setDashboardData(response.data as DashboardKitData);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || i18n.t(`${BASE}.errors.fetch_failed`),
          3.5
        );
        setDashboardData(undefined);
      }

      setDataLoading(false);
    },
    [message]
  );

  return {
    aggregateSection: dashboardData?.aggregateSection,
    turnCountByTask: dashboardData?.turnCountByTask,
    turnSummaryStatusGrid: dashboardData?.turnSummaryStatusGrid,
    turnProgressByDay: dashboardData?.turnProgressByDay,
    dataLoading,
    fetchDashboardData,
  };
}
